import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const AdvancedAdjustmentPopover = ({ isOpen, onClose, onApply, product, plan, group, relatedInstances, currentAdjustment, triggerRef, isBulkMode = false }) => {
    const { t } = useTranslation();
    const popoverRef = useRef(null);
    const [adjustmentType, setAdjustmentType] = useState('percentage');
    const [percentageValue, setPercentageValue] = useState(currentAdjustment || 0);
    const [targetPriceValue, setTargetPriceValue] = useState(plan?.currentPrice || 0);
    const [scope, setScope] = useState('thisTier'); // 'thisTier' or 'allTiers'
    const [position, setPosition] = useState({ top: 0, left: 0, placement: 'right' });
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Click outside handler
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target) &&
                triggerRef?.current &&
                !triggerRef.current.contains(event.target)
            ) {
                onClose();
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose, triggerRef]);

    // Position popover relative to trigger (desktop only)
    useEffect(() => {
        if (!isOpen || !triggerRef?.current || isMobile) return;

        const updatePosition = () => {
            if (!triggerRef?.current || !popoverRef.current) return;

            const triggerRect = triggerRef.current.getBoundingClientRect();
            const popoverRect = popoverRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Use actual dimensions, fallback to 420px width if not yet rendered
            const popoverWidth = popoverRect.width || 420;
            const popoverHeight = popoverRect.height || 400;

            let top = triggerRect.top;
            let left = triggerRect.right + 8;
            let placement = 'right';

            // Check if there's enough space on the right
            if (left + popoverWidth > viewportWidth - 16) {
                // Try left
                left = triggerRect.left - popoverWidth - 8;
                placement = 'left';

                // If not enough space on left either, place below
                if (left < 16) {
                    left = triggerRect.left;
                    top = triggerRect.bottom + 8;
                    placement = 'bottom';

                    // If not enough space below, place above
                    if (top + popoverHeight > viewportHeight - 16) {
                        top = triggerRect.top - popoverHeight - 8;
                        placement = 'top';
                    }
                }
            }

            // Ensure popover stays within viewport vertically when placed right/left
            if (placement === 'right' || placement === 'left') {
                if (top + popoverHeight > viewportHeight - 16) {
                    top = viewportHeight - popoverHeight - 16;
                }
                if (top < 16) {
                    top = 16;
                }
            }

            // Ensure popover stays within viewport horizontally when placed top/bottom
            if (placement === 'top' || placement === 'bottom') {
                if (left + popoverWidth > viewportWidth - 16) {
                    left = viewportWidth - popoverWidth - 16;
                }
                if (left < 16) {
                    left = 16;
                }
            }

            setPosition({ top, left, placement });
        };

        // Delay positioning to ensure popover is rendered with dimensions
        const timer = setTimeout(updatePosition, 0);
        
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, triggerRef, isMobile]);

    const formatPrice = (value) => {
        return new Intl.NumberFormat('sv-SE').format(Math.round(value));
    };

    const calculatedAdjustment = useMemo(() => {
        if (adjustmentType === 'percentage') {
            return parseFloat(percentageValue) || 0;
        } else {
            const target = parseFloat(targetPriceValue) || 0;
            const current = plan?.currentPrice || 0;
            if (current === 0) return 0;
            return ((target - current) / current) * 100;
        }
    }, [adjustmentType, percentageValue, targetPriceValue, plan]);

    const newPrice = useMemo(() => {
        const current = plan?.currentPrice || 0;
        return current * (1 + calculatedAdjustment / 100);
    }, [plan, calculatedAdjustment]);

    const priceChange = useMemo(() => {
        const current = plan?.currentPrice || 0;
        return newPrice - current;
    }, [newPrice, plan]);

    const handleApply = () => {
        onApply(calculatedAdjustment, scope, isBulkMode);
        onClose();
    };

    if (!isOpen || !product || !plan) return null;

    const instanceCount = relatedInstances?.length || 0;
    const hasMultipleInstances = instanceCount > 0;

    // Mobile bottom sheet
    if (isMobile) {
        return (
            <>
                <div className="popover-backdrop" onClick={onClose} />
                <div ref={popoverRef} className="advanced-adjustment-popover mobile-bottom-sheet">
                    <div className="bottom-sheet-handle" />
                    <div className="popover-content">
                        {/* Header */}
                        <div className="popover-header">
                            <h4 className="popover-title">{t('priceSimulation.adjustPrice', { name: product.name })}</h4>
                            <button type="button" className="popover-close" onClick={onClose} aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="popover-body">
                            {/* Product Info */}
                            <div className="modal-product-info">
                                <div className="modal-product-sku">{product.productSku}</div>
                                <div className="modal-product-path">{`${group?.name} → Tier 1 → ${product.name}`}</div>
                            </div>

                            {/* Current Price Box */}
                            <div className="current-price-box compact">
                                <div className="current-price-label">
                                    {t('priceSimulation.currentPrice')}: <strong>{formatPrice(plan.currentPrice)} SEK</strong>
                                </div>
                                <div className="current-price-meta">
                                    {t('priceSimulation.avgDiscount')}: {plan.averageDiscount}%
                                </div>
                            </div>

                            {/* Scope Selector - Only show in bulk mode with multiple instances */}
                            {isBulkMode && hasMultipleInstances && (
                                <div className="scope-selector-container">
                                    <div className="scope-selector-label">{t('priceSimulation.scopeLabel')}</div>
                                    <div className="scope-selector">
                                        <button
                                            type="button"
                                            className={`scope-btn ${scope === 'thisTier' ? 'active' : ''}`}
                                            onClick={() => setScope('thisTier')}
                                        >
                                            {t('priceSimulation.thisTierOnly')}
                                        </button>
                                        <button
                                            type="button"
                                            className={`scope-btn ${scope === 'allTiers' ? 'active' : ''}`}
                                            onClick={() => setScope('allTiers')}
                                        >
                                            {t('priceSimulation.allTiers', { count: instanceCount + 1 })}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Adjustment Type Toggle */}
                            <div className="adjustment-type-section">
                                <label className="radio-option">
                                    <input
                                        type="radio"
                                        name="adjustmentType"
                                        checked={adjustmentType === 'percentage'}
                                        onChange={() => setAdjustmentType('percentage')}
                                    />
                                    <span className="radio-label">{t('priceSimulation.percentageAdjustment')}</span>
                                </label>
                                {adjustmentType === 'percentage' && (
                                    <div className="adjustment-input-group">
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={percentageValue}
                                            onChange={(e) => setPercentageValue(e.target.value)}
                                            step="0.1"
                                        />
                                    </div>
                                )}
                                <div className="adjustment-preview">
                                    → {t('priceSimulation.newPrice')}: {formatPrice(newPrice)} SEK
                                </div>
                            </div>

                            <div className="adjustment-type-section">
                                <label className="radio-option">
                                    <input
                                        type="radio"
                                        name="adjustmentType"
                                        checked={adjustmentType === 'target'}
                                        onChange={() => setAdjustmentType('target')}
                                    />
                                    <span className="radio-label">{t('priceSimulation.targetPrice')}</span>
                                </label>
                                {adjustmentType === 'target' && (
                                    <div className="adjustment-input-group">
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={targetPriceValue}
                                            onChange={(e) => setTargetPriceValue(e.target.value)}
                                            step="1"
                                        />
                                    </div>
                                )}
                                <div className="adjustment-preview">
                                    → {t('priceSimulation.change')}: {priceChange >= 0 ? '+' : ''}
                                    {calculatedAdjustment.toFixed(1)}% ({priceChange >= 0 ? '+' : ''}
                                    {formatPrice(priceChange)} SEK)
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="popover-footer">
                            <button type="button" className="btn btn-default" onClick={onClose}>
                                {t('priceSimulation.cancel')}
                            </button>
                            <button type="button" className="btn btn-primary" onClick={handleApply}>
                                {t('priceSimulation.apply')}
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Desktop popover
    return (
        <div
            ref={popoverRef}
            className={`advanced-adjustment-popover placement-${position.placement}`}
            style={{
                position: 'fixed',
                top: `${position.top}px`,
                left: `${position.left}px`,
                zIndex: 1000,
            }}
        >
            {/* Popover Arrow */}
            <div className={`popover-arrow arrow-${position.placement}`} />

            <div className="popover-content">
                {/* Header */}
                <div className="popover-header">
                    <h4 className="popover-title">{t('priceSimulation.adjustPrice', { name: product.name })}</h4>
                    <button type="button" className="popover-close" onClick={onClose} aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                {/* Body */}
                <div className="popover-body">
                    {/* Product Info */}
                    <div className="modal-product-info">
                        <div className="modal-product-sku">{product.productSku}</div>
                        <div className="modal-product-path">{`${group?.name} → Tier 1 → ${product.name}`}</div>
                    </div>

                    {/* Current Price Box */}
                    <div className="current-price-box compact">
                        <div className="current-price-label">
                            {t('priceSimulation.currentPrice')}: <strong>{formatPrice(plan.currentPrice)} SEK</strong>
                        </div>
                        <div className="current-price-meta">
                            {t('priceSimulation.avgDiscount')}: {plan.averageDiscount}%
                        </div>
                    </div>

                    {/* Scope Selector - Only show in bulk mode with multiple instances */}
                    {isBulkMode && hasMultipleInstances && (
                        <div className="scope-selector-container">
                            <div className="scope-selector-label">{t('priceSimulation.scopeLabel')}</div>
                            <div className="scope-selector">
                                <button
                                    type="button"
                                    className={`scope-btn ${scope === 'thisTier' ? 'active' : ''}`}
                                    onClick={() => setScope('thisTier')}
                                >
                                    {t('priceSimulation.thisTierOnly')}
                                </button>
                                <button
                                    type="button"
                                    className={`scope-btn ${scope === 'allTiers' ? 'active' : ''}`}
                                    onClick={() => setScope('allTiers')}
                                >
                                    {t('priceSimulation.allTiers', { count: instanceCount + 1 })}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Adjustment Type Toggle */}
                    <div className="adjustment-type-section">
                        <label className="radio-option">
                            <input
                                type="radio"
                                name="adjustmentType"
                                checked={adjustmentType === 'percentage'}
                                onChange={() => setAdjustmentType('percentage')}
                            />
                            <span className="radio-label">{t('priceSimulation.percentageAdjustment')}</span>
                        </label>
                        {adjustmentType === 'percentage' && (
                            <div className="adjustment-input-group">
                                <input
                                    type="number"
                                    className="form-control"
                                    value={percentageValue}
                                    onChange={(e) => setPercentageValue(e.target.value)}
                                    step="0.1"
                                />
                            </div>
                        )}
                        <div className="adjustment-preview">
                            → {t('priceSimulation.newPrice')}: {formatPrice(newPrice)} SEK
                        </div>
                    </div>

                    <div className="adjustment-type-section">
                        <label className="radio-option">
                            <input
                                type="radio"
                                name="adjustmentType"
                                checked={adjustmentType === 'target'}
                                onChange={() => setAdjustmentType('target')}
                            />
                            <span className="radio-label">{t('priceSimulation.targetPrice')}</span>
                        </label>
                        {adjustmentType === 'target' && (
                            <div className="adjustment-input-group">
                                <input
                                    type="number"
                                    className="form-control"
                                    value={targetPriceValue}
                                    onChange={(e) => setTargetPriceValue(e.target.value)}
                                    step="1"
                                />
                            </div>
                        )}
                        <div className="adjustment-preview">
                            → {t('priceSimulation.change')}: {priceChange >= 0 ? '+' : ''}
                            {calculatedAdjustment.toFixed(1)}% ({priceChange >= 0 ? '+' : ''}
                            {formatPrice(priceChange)} SEK)
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="popover-footer">
                    <button type="button" className="btn btn-default" onClick={onClose}>
                        {t('priceSimulation.cancel')}
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleApply}>
                        {t('priceSimulation.apply')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdvancedAdjustmentPopover;
